"use client";

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Champ « Adresse » avec autocomplétion française.
 *
 * Source : api-adresse.data.gouv.fr — API officielle, gratuite, sans clé ni
 * quota (Base Adresse Nationale). France uniquement, ce qui suffit pour la
 * facturation de clients français ; le champ Pays reste saisi à part.
 *
 * On saisit la rue ; choisir une suggestion remplit rue + code postal + ville
 * d'un coup via `onSelect`. La saisie reste libre : si aucune suggestion ne
 * convient (adresse étrangère, lieu-dit…), l'utilisateur tape comme avant.
 */

interface Suggestion {
  label: string;
  name: string; // rue + numéro
  postcode: string;
  city: string;
}

interface Props {
  value: string;
  onChange: (rue: string) => void;
  onSelect: (a: { address: string; postalCode: string; city: string }) => void;
  id?: string;
  placeholder?: string;
  className?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  id,
  placeholder,
  className,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [ouvert, setOuvert] = useState(false);
  // Vrai uniquement quand la valeur change par une FRAPPE de l'utilisateur.
  // Un remplissage par programme (choix d'une suggestion, ou autocomplétion
  // d'entreprise qui pose l'adresse du siège) laisse ce drapeau à faux : la
  // liste ne se rouvre donc pas toute seule.
  const saisieManuelle = useRef(false);
  const boiteRef = useRef<HTMLDivElement>(null);

  // Recherche anti-rebond : on n'interroge l'API qu'après 250 ms de pause, et
  // seulement au-delà de 3 caractères — sinon on la martèle à chaque frappe.
  useEffect(() => {
    if (!saisieManuelle.current) return;
    const q = value.trim();
    const annule = new AbortController();
    // Sous 3 caractères : on vide via le timer (pas synchronement dans le corps
    // de l'effet), pour ne pas déclencher un rendu en cascade.
    if (q.length < 3) {
      const vide = setTimeout(() => setSuggestions([]), 0);
      return () => clearTimeout(vide);
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5&type=housenumber`,
          { signal: annule.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        const items: Suggestion[] = (data.features ?? []).map(
          (f: { properties: Suggestion }) => f.properties
        );
        setSuggestions(items);
        setOuvert(true);
      } catch {
        // Réseau coupé ou requête annulée : on laisse la saisie manuelle.
      }
    }, 250);
    return () => {
      clearTimeout(t);
      annule.abort();
    };
  }, [value]);

  // Fermer la liste au clic en dehors.
  useEffect(() => {
    const dehors = (e: MouseEvent) => {
      if (boiteRef.current && !boiteRef.current.contains(e.target as Node)) {
        setOuvert(false);
      }
    };
    document.addEventListener("mousedown", dehors);
    return () => document.removeEventListener("mousedown", dehors);
  }, []);

  const choisir = (s: Suggestion) => {
    saisieManuelle.current = false;
    onSelect({ address: s.name, postalCode: s.postcode, city: s.city });
    setOuvert(false);
    setSuggestions([]);
  };

  return (
    <div ref={boiteRef} className="relative">
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          saisieManuelle.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => suggestions.length > 0 && setOuvert(true)}
        className={className}
      />
      {ouvert && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-border bg-[var(--bg-secondary)] shadow-lg">
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              type="button"
              onClick={() => choisir(s)}
              className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <span className="text-foreground">{s.name}</span>
              <span className="text-xs text-muted-foreground">
                {s.postcode} {s.city}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
